import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Layout from "../components/Layout";
import CallsTable, { SortField, SortDirection } from "../components/grants/calls/CallsTable";
import CallsColumnsPanel from "../components/grants/calls/CallsColumnsPanel";
import { useCallsColumnConfig } from "../components/grants/calls/useCallsColumnConfig";
import CallsFilters from "../components/grants/calls/CallsFilters";
import { CallLabelManagementModal, useCallLabels } from "../components/grants/labels";
import apiClient from "../services/api";
import { useDebounce } from "../hooks/useDebounce";
import { useTurnstileContext } from "../hooks/useTurnstile";
import { CallItem, Pagination, StatusCount, ProgramCount, CompetitionCount, LabelCount } from "../types/grants";
import { normalizeRegion } from "../constants/regions";
import { useUniqueRegions } from "../hooks/useUniqueRegions";
import { useHotel } from "../contexts/HotelContext";
import { EmptyState } from "../components/common/EmptyState";
import { TurnstileErrorMessage } from "../components/grants/TurnstileErrorMessage";
import { formatNaboryCount } from "../utils/text.utils";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Allowed sortBy values (must match backend whitelist) */
const ALLOWED_SORT_FIELDS: SortField[] = ["endDate", "startDate", "createdAt", "title"];

interface CallFilters {
  page?: number;
  limit?: number;
  /** Competition IDs for main dropdown (multiselect) */
  competitionIds?: string[];
  statuses?: string[];
  regions?: string[];
  search?: string;
  /** Label IDs for filtering by specific labels */
  labelIds?: string[];
  /** Label filter mode: 'labeled' or 'unlabeled' */
  labelFilter?: string;
}

interface CallsResponse {
  calls: CallItem[];
  pagination: Pagination;
  statusCounts: StatusCount[];
  programCounts: ProgramCount[];
  competitionCounts: CompetitionCount[];
  labelCounts?: LabelCount[];
  allCalls?: CallItem[];
}

/**
 * Page component for displaying archived (inactive) grant calls.
 * Shows calls with isActive=false or status CLOSED/RESOLVED.
 */
const EMPTY_CALL_IDS: string[] = [];

const GrantsCallsArchive: React.FC = () => {
  const { hasModuleEnabled, user } = useHotel();
  // M4 (review): Świadoma akceptacja — niezalogowani użytkownicy współdzielą
  // klucz `calls-column-config-v1-anonymous` w localStorage w danej przeglądarce.
  // Brak PII (tylko konfiguracja widoku kolumn). Po zalogowaniu hook czyta klucz
  // per-user. Patrz: docs/specifications/2026-05-27-konfiguracja-widoku-naborow/.
  const userId = user?.id ?? "anonymous";
  const isAuthenticated = !!user?.id;
  const { isCookieReady } = useTurnstileContext();
  const hasLabelsModule = hasModuleEnabled("grants:schedule");
  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);
  const [searchParams] = useSearchParams();

  // Column configuration (per-user localStorage). Hoisted from CallsTable
  // so the "Kolumny" trigger lives next to filter bar (spec: §Trigger "Kolumny").
  const {
    visibleColumns,
    allColumns,
    toggleColumn,
    reorderColumns,
    resetToDefault,
  } = useCallsColumnConfig(userId);
  const [isColumnsPanelOpen, setIsColumnsPanelOpen] = useState(false);
  const columnsTriggerRef = useRef<HTMLButtonElement>(null);

  // Read sorting state from URL (managed by CallsTable's setSearchParams)
  const sortBy = useMemo(() => {
    const raw = searchParams.get("sortBy");
    return raw && ALLOWED_SORT_FIELDS.includes(raw as SortField) ? (raw as SortField) : undefined;
  }, [searchParams]);
  const sortOrder = useMemo(() => {
    const raw = searchParams.get("sortOrder");
    return raw === "ASC" || raw === "DESC" ? (raw as SortDirection) : undefined;
  }, [searchParams]);

  const [filters, setFilters] = useState<CallFilters>({
    page: 1,
    limit: 50,
  });

  // Load filters from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("grants-calls-archive-filters");
    if (saved) {
      try {
        const savedFilters = JSON.parse(saved);
        setFilters((prev) => ({
          ...prev,
          competitionIds: savedFilters.competitionIds || [],
          statuses: savedFilters.statuses || [],
          regions: (savedFilters.regions || []).map(normalizeRegion),
          search: savedFilters.search,
          labelIds: savedFilters.labelIds?.filter((id: string) => UUID_RE.test(id)) || undefined,
          labelFilter: savedFilters.labelFilter || undefined,
        }));
      } catch (error) {
        console.error("Failed to parse saved filters:", error);
        localStorage.removeItem("grants-calls-archive-filters");
      }
    }
  }, []);

  // Save filters to localStorage when they change
  useEffect(() => {
    const filtersToSave = {
      competitionIds: filters.competitionIds,
      statuses: filters.statuses,
      regions: filters.regions,
      search: filters.search,
      labelIds: filters.labelIds,
      labelFilter: filters.labelFilter,
    };
    localStorage.setItem(
      "grants-calls-archive-filters",
      JSON.stringify(filtersToSave)
    );
  }, [filters.competitionIds, filters.statuses, filters.regions, filters.search, filters.labelIds, filters.labelFilter]);

  const debouncedFilters = useDebounce(filters, 300);

  const { data, isLoading, isFetching, error, refetch } = useQuery<CallsResponse>({
    queryKey: ["grants-calls-archive", debouncedFilters, sortBy, sortOrder],
    // Gate: don't fetch until Turnstile cookie is set (prevents 403 on first render)
    enabled: isCookieReady,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedFilters.page)
        params.append("page", debouncedFilters.page.toString());
      if (debouncedFilters.limit)
        params.append("limit", debouncedFilters.limit.toString());

      // Main filter: competitionIds (multiselect)
      if (debouncedFilters.competitionIds && debouncedFilters.competitionIds.length > 0) {
        debouncedFilters.competitionIds.forEach(id => params.append("competitionIds", id));
      }

      // Handle multiple statuses
      if (debouncedFilters.statuses && debouncedFilters.statuses.length > 0) {
        debouncedFilters.statuses.forEach(status => params.append("status", status));
      }

      // Handle multiple regions
      if (debouncedFilters.regions && debouncedFilters.regions.length > 0) {
        debouncedFilters.regions.forEach(region => params.append("region", region));
      }

      if (debouncedFilters.search)
        params.append("search", debouncedFilters.search);
      // Label filters
      debouncedFilters.labelIds?.forEach((id) => params.append("labelId", id));
      if (debouncedFilters.labelFilter) {
        params.append("labelFilter", debouncedFilters.labelFilter);
      }
      // Server-side sorting
      if (sortBy) {
        params.append("sortBy", sortBy);
        if (sortOrder) {
          params.append("sortOrder", sortOrder);
        }
      }
      // Endpoint /archive automatically filters CLOSED/RESOLVED calls

      const { data } = await apiClient.get(`/grants/calls/archive?${params}`);
      return data;
    },
  });

  // Use call labels hook for label management (only when module is enabled)
  const callIds = useMemo(
    () => (data?.calls || []).map((c) => c.id),
    [data?.calls]
  );
  const {
    labels: callLabels,
    articleLabels,
    createLabel: handleCreateLabel,
    createAndAssign: handleCreateAndAssign,
    updateLabel: handleUpdateLabel,
    deleteLabel: handleDeleteLabel,
    setCallLabel: handleSetCallLabel,
  } = useCallLabels({ callIds: hasLabelsModule ? callIds : EMPTY_CALL_IDS });

  const uniqueRegions = useUniqueRegions(data?.calls || [], data?.allCalls);

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleCompetitionChange = (competitionIds: string[]) => {
    setFilters((prev) => ({ ...prev, competitionIds, page: 1 }));
  };

  const handleStatusChange = (statuses: string[]) => {
    setFilters((prev) => ({ ...prev, statuses, page: 1 }));
  };

  const handleRegionChange = (regions: string[]) => {
    setFilters((prev) => ({ ...prev, regions, page: 1 }));
  };

  const handleSearchChange = (search: string) => {
    setFilters((prev) => ({ ...prev, search, page: 1 }));
  };

  const handleLabelFilterChange = useCallback(
    (labelIds: string[], labelFilter: string | undefined) => {
      setFilters((prev) => ({ ...prev, labelIds: labelIds.length > 0 ? labelIds : undefined, labelFilter, page: 1 }));
    },
    []
  );

  const handleClearFilters = () => {
    setFilters({ page: 1, limit: filters.limit });
    localStorage.removeItem("grants-calls-archive-filters");
  };

  // Treat cookie-not-ready as loading (query is disabled, so isLoading stays false)
  const effectivelyLoading = isLoading || !isCookieReady;

  const hasActiveFilters =
    (filters.competitionIds && filters.competitionIds.length > 0) ||
    (filters.statuses && filters.statuses.length > 0) ||
    (filters.regions && filters.regions.length > 0) ||
    (filters.labelIds && filters.labelIds.length > 0) ||
    !!filters.labelFilter ||
    filters.search;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 text-left">
            Nabory - Archiwalne
          </h1>
          <p className="mt-1 text-sm text-neutral-600 text-left">
            Lista zamkniętych i zarchiwizowanych naborów
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {effectivelyLoading ? (
            <div className="p-6 text-center" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">
                Ładowanie naborów...
              </p>
            </div>
          ) : error ? (
            <div className="p-6 text-center" role="alert">
              <div className="text-red-600 mb-4">
                <svg
                  className="h-12 w-12 mx-auto mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p className="font-medium">Błąd podczas pobierania naborów</p>
                <TurnstileErrorMessage error={error} />
              </div>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Spróbuj ponownie
              </button>
            </div>
          ) : (
            <div className="p-6">
              {/* Filters + "Kolumny" trigger (spec: pozycja po prawej obok paska filtrów) */}
              <div className="flex items-start gap-2 relative">
                <div className="flex-1 min-w-0">
                  <CallsFilters
                    competitionCounts={data?.competitionCounts}
                    statusCounts={data?.statusCounts}
                    regions={uniqueRegions}
                    selectedCompetitionIds={filters.competitionIds || []}
                    selectedStatuses={filters.statuses || []}
                    selectedRegions={filters.regions || []}
                    searchQuery={filters.search}
                    isFetching={isFetching && !!data}
                    onCompetitionChange={handleCompetitionChange}
                    onStatusChange={handleStatusChange}
                    onRegionChange={handleRegionChange}
                    onSearchChange={handleSearchChange}
                    onClearFilters={handleClearFilters}
                    totalCount={data?.pagination?.total ?? data?.calls?.length}
                    labelCounts={hasLabelsModule ? data?.labelCounts : undefined}
                    selectedLabelIds={filters.labelIds || []}
                    selectedLabelFilter={filters.labelFilter}
                    onLabelFilterChange={hasLabelsModule ? handleLabelFilterChange : undefined}
                  />
                </div>
                {/* Spec: trigger "Kolumny" tylko dla zalogowanych użytkowników (per-user localStorage). */}
                {isAuthenticated && (
                  <div className="relative pt-0">
                    <button
                      ref={columnsTriggerRef}
                      onClick={() => setIsColumnsPanelOpen((prev) => !prev)}
                      aria-expanded={isColumnsPanelOpen}
                      aria-haspopup="dialog"
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                      Kolumny
                    </button>
                    {isColumnsPanelOpen && (
                      <CallsColumnsPanel
                        allColumns={allColumns}
                        visibleColumns={visibleColumns}
                        onToggle={toggleColumn}
                        onReorder={reorderColumns}
                        onReset={resetToDefault}
                        forcedVisibleIds={hasLabelsModule ? ["label"] : []}
                        onClose={() => {
                          setIsColumnsPanelOpen(false);
                          columnsTriggerRef.current?.focus();
                        }}
                        triggerRef={columnsTriggerRef}
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="mt-4 mb-4 flex items-center justify-between">
                <p className="text-sm text-neutral-600" aria-live="polite">
                  {formatNaboryCount(data?.pagination?.total ?? data?.calls?.length ?? 0)}
                  {isFetching && (
                    <span className="ml-2 inline-flex items-center">
                      <span className="animate-spin h-3 w-3 border-2 border-primary-600 border-t-transparent rounded-full mr-1"></span>
                      <span className="text-neutral-400">aktualizacja...</span>
                    </span>
                  )}
                </p>
                <select
                  value={filters.limit || 50}
                  onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                  className="px-3 py-1 text-sm border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:outline-none"
                >
                  <option value={25}>25 na stronę</option>
                  <option value={50}>50 na stronę</option>
                  <option value={100}>100 na stronę</option>
                </select>
              </div>

              {data?.calls?.length === 0 ? (
                <EmptyState
                  icon={
                    <svg
                      className="mx-auto h-12 w-12"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                      />
                    </svg>
                  }
                  title="Brak archiwalnych naborów"
                  action={
                    hasActiveFilters
                      ? { label: "Wyczyść filtry", onClick: handleClearFilters }
                      : undefined
                  }
                />
              ) : (
                <>
                  <CallsTable
                    calls={data?.calls || []}
                    hideStatusColumn={filters.statuses?.length === 1}
                    userId={userId}
                    visibleColumns={visibleColumns}
                    allColumns={allColumns}
                    labels={hasLabelsModule ? callLabels : undefined}
                    articleLabels={hasLabelsModule ? articleLabels : undefined}
                    onSetCallLabel={hasLabelsModule ? async (callId, labelId) => {
                      const result = await handleSetCallLabel(callId, labelId);
                      refetch();
                      return result;
                    } : undefined}
                    onCreateAndAssign={hasLabelsModule ? async (name, callId) => {
                      const result = await handleCreateAndAssign(name, callId);
                      refetch();
                      return result;
                    } : undefined}
                    onManageLabels={hasLabelsModule ? () => setIsLabelModalOpen(true) : undefined}
                  />

                  {data?.pagination && data.pagination.totalPages > 1 && (
                    <nav
                      className="mt-6 flex items-center justify-between"
                      role="navigation"
                      aria-label="Paginacja naborów"
                    >
                      <button
                        onClick={() =>
                          handlePageChange(data.pagination.currentPage - 1)
                        }
                        disabled={!data.pagination.hasPrevious}
                        aria-label="Przejdź do poprzedniej strony"
                        className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-primary-500 focus:outline-none"
                      >
                        Poprzednia
                      </button>
                      <span
                        className="text-sm text-neutral-600"
                        aria-current="page"
                      >
                        Strona {data.pagination.currentPage} z{" "}
                        {data.pagination.totalPages}
                      </span>
                      <button
                        onClick={() =>
                          handlePageChange(data.pagination.currentPage + 1)
                        }
                        disabled={!data.pagination.hasNext}
                        aria-label="Przejdź do następnej strony"
                        className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-primary-500 focus:outline-none"
                      >
                        Następna
                      </button>
                    </nav>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Label Management Modal */}
        {hasLabelsModule && (
          <CallLabelManagementModal
            isOpen={isLabelModalOpen}
            onClose={() => setIsLabelModalOpen(false)}
            labels={callLabels}
            labelCounts={data?.labelCounts}
            onUpdateLabel={async (id, payload) => {
              await handleUpdateLabel(id, payload);
              refetch();
            }}
            onDeleteLabel={async (id) => {
              await handleDeleteLabel(id);
              refetch();
            }}
            onCreateLabel={async (name, color) => {
              await handleCreateLabel(name, color);
              refetch();
            }}
          />
        )}
      </div>
    </Layout>
  );
};

export default GrantsCallsArchive;
